import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postCreateServicesMaster } from "apis";
import { AxonApiResponse, GetServiceAutosuggest } from "../../types/apis";
import { useReceiptStore } from "@elixir/stores";
import useBillingStore from "../../stores/billing-store/billing-store";
import { getServicesMasterAutosuggestQuery } from "./services/use-get-services-master-autosuggest";
import { useMyAccessProfileV2 } from "../tryst/get-my-access-profile";

const usePostCreateService = () => {
  const queryClient = useQueryClient();
  const { data: myAccessProfile } = useMyAccessProfileV2();

  // Get clinic_id from receipt store or billing store
  const receiptStoreClinicId = useReceiptStore((state) => state.clinicId);
  const { clinicId: billingStoreClinicId } = useBillingStore();
  const clinic_id = receiptStoreClinicId || billingStoreClinicId || "";

  return useMutation<
    AxonApiResponse<GetServiceAutosuggest>,
    Error,
    {
      name: string;
      price?: number;
    }
  >(
    async (data) => {
      const response = await postCreateServicesMaster({
        service_name: data.name,
        price: data?.price ?? 0,
        clinic_id: clinic_id,
      });
      // const response = await postCreateServicesMaster(payload);

      if (!response.success) {
        return {
          success: false,
          data: null,
        } as any;
      }

      // Check if the response contains the service data
      const responseData = response.data as any;
      if (responseData?.id || responseData?.service_id) {
        return {
          success: true,
          data: {
            id: responseData.id || responseData.service_id,
            name: data.name,
          } as GetServiceAutosuggest,
        };
      }

      // If response doesn't have service data, refetch from autosuggest to get the real service
      if (clinic_id && myAccessProfile?.business?.id) {
        // Refetch the autosuggest query to get the newly created service
        const refetchResult = await queryClient.refetchQueries(
          getServicesMasterAutosuggestQuery({
            owner_id: myAccessProfile.business.id,
            clinic_id: clinic_id,
            query: data.name,
          })
        );

        // Get the refetched data from cache
        const queryData = queryClient.getQueryData(
          getServicesMasterAutosuggestQuery({
            owner_id: myAccessProfile.business.id,
            clinic_id: clinic_id,
            query: data.name,
          })
        ) as any;

        // Find the newly created service by name
        const createdService = queryData?.data?.find(
          (service: any) => service.service_name === data.name
        );

        if (createdService) {
          return {
            success: true,
            data: {
              id:
                createdService.service_id?.toString() ||
                createdService.id?.toString(),
              name: createdService.service_name || data.name,
            } as GetServiceAutosuggest,
          };
        }
      }

      // If we still don't have the service, the component will handle it via autosuggest
      // Return the service name so the component can use it
      return {
        success: true,
        data: {
          id: "", // Component will get the real ID from autosuggest
          name: data.name,
        } as GetServiceAutosuggest,
      };
    },
    {
      onSuccess: async (response, variables) => {
        // Invalidate the services master autosuggest query to refetch with the new service
        if (clinic_id && myAccessProfile?.business?.id) {
          await queryClient.invalidateQueries(
            getServicesMasterAutosuggestQuery({
              owner_id: myAccessProfile.business.id,
              clinic_id: clinic_id,
            })
          );
        }
        // Also invalidate the old query for backward compatibility
        queryClient.invalidateQueries({
          queryKey: [`get-service-autosuggest-${variables.name}`],
          exact: true,
        });
      },
    }
  );
};

export default usePostCreateService;
